import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { isApiError, moduleOfferAuthoringApi } from "@bw/shared/api";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  CommercialOfferDraft,
  CommercialOfferDraftWrite,
} from "@bw/shared/types/generated";
import { PlatformPage } from "@/components/PlatformPage";
import { ModuleOfferEditor } from "../components/ModuleOfferEditor";
import type { ModuleOfferDraftSaveAttempt } from "../components/moduleOfferAuthoringTypes";
import {
  useModuleOfferDraft,
  useCreateModuleOfferDraft,
  useReplaceModuleOfferDraft,
} from "../hooks/useModuleOfferAuthoring";

function inputs(draft: CommercialOfferDraft): CommercialOfferDraftWrite {
  const {
    draft_id: _id,
    revision: _revision,
    updated_at: _updated,
    unresolved_gates: _gates,
    ...write
  } = draft;
  return write;
}

function DraftController({
  initial,
  draftId,
  reload,
  onAuthorityDenied,
}: {
  initial: CommercialOfferDraft | null;
  draftId?: string;
  reload: () => Promise<CommercialOfferDraft | null>;
  onAuthorityDenied: () => void;
}) {
  const { user } = useAuth();
  const actor = user?.uid;
  const live = useRef({ actor, draftId, active: true }); live.current.actor = actor; live.current.draftId = draftId;
  useLayoutEffect(() => { live.current.active = true; return () => { live.current.active = false; }; }, [actor, draftId]);
  const current = () => live.current.active && live.current.actor === actor && live.current.draftId === draftId;
  const saving = useRef(false);
  const [captured, setCaptured] = useState(initial);
  const [value, setValue] = useState<CommercialOfferDraftWrite | null>(() =>
    initial ? inputs(initial) : null,
  );
  const [epoch, setEpoch] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [attempt, setAttempt] = useState<ModuleOfferDraftSaveAttempt | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const create = useCreateModuleOfferDraft(),
    replace = useReplaceModuleOfferDraft(draftId);
  const navigate = useNavigate();
  const mutation = captured ? replace : create;
  const stale = isApiError(mutation.error) && mutation.error.status === 409;
  const denied = isApiError(mutation.error) && mutation.error.status === 403;
  useEffect(() => {
    if (denied) onAuthorityDenied();
  }, [denied, onAuthorityDenied]);
  function adopt(draft: CommercialOfferDraft) {
    if (!current() || draft.terms_format !== "software_terms_v2" || draftId && draft.draft_id !== draftId) return;
    setCaptured(draft);
    setValue(inputs(draft));
    setAttempt(null); setSaveError(null);
    setEpoch((previous) => previous + 1);
  }
  async function save() {
    if (!current() || saving.current || mutation.isPending || stale || denied || reloading || !attempt && !value) return;
    let work = attempt;
    if (work === null) {
      if (value === null) return;
      const data = structuredClone(value);
      work = captured && draftId ? { kind: "replace", draftId, request: { expected_revision: captured.revision, data } }
        : { kind: "create", data };
    }
    saving.current = true; setAttempt(work); setSaveError(null);
    try {
      const result = work.kind === "replace" ? await replace.mutateAsync(work.request) : await create.mutateAsync(work.data);
      if (!current()) return;
      if (result.terms_format !== "software_terms_v2" || work.kind === "replace" && result.draft_id !== work.draftId)
        throw new Error("Draft response does not match the captured resource");
      if (work.kind === "replace") adopt(result);
      else { setAttempt(null); navigate(`/contracts/module-offers/${result.draft_id}`); }
    } catch (error) {
      if (!current()) return;
      const terminal = isApiError(error) && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status);
      if (terminal && error.status !== 409 && error.status !== 403) setAttempt(null);
      setSaveError(terminal ? "The captured draft save was denied. Review its current source before another attempt."
        : "Draft save outcome is unknown. Retry the captured original inputs; no deduplication is implied.");
    } finally { saving.current = false; }
  }
  if (denied)
    return (
      <Alert severity="warning">Current Admin draft access unavailable.</Alert>
    );
  return (
    <Stack spacing={2}>
      {captured ? (
        <Typography variant="body2">Revision {captured.revision}</Typography>
      ) : null}
      {stale ? (
        <Alert severity="warning">
          Draft changed. Reload and review before saving again.{" "}
          <Button
            size="small"
            disabled={reloading || mutation.isPending}
            onClick={async () => {
              setReloading(true);
              try {
                const source = await reload();
                if (current() && source && source.draft_id === draftId) {
                  adopt(source);
                  mutation.reset();
                }
              } finally {
                if (current()) setReloading(false);
              }
            }}
          >
            Reload draft
          </Button>
        </Alert>
      ) : null}
      {attempt && !mutation.isPending && !stale ? <Alert severity="warning">
        {saveError ?? "The original draft save is retained."}
        <Button size="small" disabled={reloading} onClick={() => { void save(); }}>Retry original draft save</Button>
      </Alert> : null}
      <ModuleOfferEditor
        key={epoch}
        value={value}
        onChange={setValue}
        isPending={mutation.isPending || stale || reloading || attempt !== null}
        error={
          saveError ?? (mutation.isError
            ? "Could not save this draft. Review its current source before retrying."
            : null)
        }
        onSave={() => { void save(); }}
      />
    </Stack>
  );
}

function DraftAccessBoundary({
  draftId,
  query,
}: {
  draftId: string | undefined;
  query: UseQueryResult<CommercialOfferDraft, Error>;
}) {
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewedDraft, setReviewedDraft] = useState<CommercialOfferDraft | null>(null);
  const denied =
    query.isError && isApiError(query.error) && query.error.status === 403;
  const latchDenial = useCallback(() => setAuthorityDenied(true), []);
  useEffect(() => {
    if (denied) latchDenial();
  }, [denied, latchDenial]);
  if (denied || authorityDenied)
    return (
      <Alert severity="warning">
        Current Admin draft access unavailable. Review current authority before
        continuing.{" "}
        <Button
          size="small"
          disabled={reviewing}
          onClick={async () => {
            setReviewing(true);
            try {
              if (draftId) {
                const result = await query.refetch();
                if (result.isSuccess && result.data.draft_id === draftId) {
                  setReviewedDraft(result.data);
                  setAuthorityDenied(false);
                }
              } else {
                // No draft exists yet. The existing Admin catalogue read obtains
                // current server proof without fabricating a draft/capability fact.
                await moduleOfferAuthoringApi.listDrafts();
                setAuthorityDenied(false);
              }
            } catch {
              // Keep the same scoped denial fence; a source failure is not authority.
            } finally {
              setReviewing(false);
            }
          }}
        >
          Review current access
        </Button>
      </Alert>
    );
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Drafts do not approve their own product, legal or release terms.
      </Typography>
      {draftId && query.isError ? (
        <Alert severity="warning">
          Current module draft unavailable.{" "}
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {!draftId || (query.data && query.data.draft_id === draftId) ? (
        <DraftController
          initial={reviewedDraft ?? query.data ?? null}
          draftId={draftId}
          onAuthorityDenied={latchDenial}
          reload={async () => {
            const result = await query.refetch();
            return result.isSuccess ? result.data : null;
          }}
        />
      ) : null}
      {draftId && !query.isError ? (
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={`/contracts/module-offers/${draftId}/publication`}
        >
          Review publication candidate
        </Button>
      ) : null}
    </Stack>
  );
}

export function ModuleOfferDraftEditorPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { user } = useAuth();
  const query = useModuleOfferDraft(draftId);
  return (
    <PlatformPage
      title={draftId ? "Edit module offer draft" : "New module offer draft"}
      isLoading={Boolean(draftId && query.isPending)}
      actions={
        <Button size="small" component={Link} to="/contracts/module-offers">
          Back to module drafts
        </Button>
      }
    >
      <DraftAccessBoundary
        key={`${user?.uid}:${draftId ?? "new"}`}
        draftId={draftId}
        query={query}
      />
    </PlatformPage>
  );
}
